import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore, Play, Info, Sparkles, Loader2 } from "lucide-react";
import SetupHealthCard from "@/components/SetupHealthCard";
import CoverageRuleEditor  from "@/components/CoverageRuleEditor";
import LeaveRuleEditor     from "@/components/LeaveRuleEditor";
import FairnessRuleEditor  from "@/components/FairnessRuleEditor";
import PolicyPreviewDialog from "@/components/PolicyPreviewDialog";
import { useHashHighlight } from "@/lib/highlightRow";

// Coverage, Leave & Fairness policies. Captured for FUTURE
// scheduling automation. STRICTLY ADDITIVE, not consumed by rota / leave
// decision flow / swap matcher / assignment writes today.

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const SCOPE_TONE = { company: "blue", team: "amber", category: "green" };
const METRIC_LABEL = {
  weekend_count: "Weekend count",
  night_count: "Night count",
  holiday_count: "Holiday count",
  overtime_hours: "Overtime hours",
  category_count: "Category count",
  leave_approval_count: "Leave approvals",
};

export default function PoliciesPage() {
  const [tab, setTab] = useState("coverage");
  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeOnly, setActiveOnly] = useState(true);

  // per-tab data
  const [coverage, setCoverage] = useState([]);
  const [leave, setLeave] = useState([]);
  const [fairness, setFairness] = useState([]);

  // shared editor & preview state
  const [editor, setEditor] = useState({ open: false, kind: null, editing: null });
  const [preview, setPreview] = useState({ open: false, kind: null, rule: null });

  const loadCommon = async () => {
    try {
      const [{ data: c }, { data: t }] = await Promise.all([
        api.get("/categories"),
        api.get("/teams"),
      ]);
      setCategories(c);
      setTeams(t);
    } catch { /* ignore */ }
  };

  const loadCoverage = async () => {
    const { data } = await api.get(`/coverage-rules?active_only=${activeOnly}`);
    setCoverage(data);
  };
  const loadLeave = async () => {
    const { data } = await api.get(`/leave-rules?active_only=${activeOnly}`);
    setLeave(data);
  };
  const loadFairness = async () => {
    const { data } = await api.get(`/fairness-rules?active_only=${activeOnly}`);
    setFairness(data);
  };

  useEffect(() => { loadCommon(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (tab === "coverage") loadCoverage();
    if (tab === "leave") loadLeave();
    if (tab === "fairness") loadFairness();
    // eslint-disable-next-line
  }, [tab, activeOnly]);

  // Phase 2Z — accept a `?category_id=<id>&new=1` query param so the
  // Setup/Readiness "Open fix" buttons land on the coverage tab with
  // the new-rule editor pre-filled for the right category.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const want = searchParams.get("new");
    const cid = searchParams.get("category_id");
    if (want === "1" && cid) {
      setTab("coverage");
      setEditor({ open: true, kind: "coverage",
                   editing: { category_id: cid } });
      const next = new URLSearchParams(searchParams);
      next.delete("new"); next.delete("category_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaved = () => {
    setEditor({ open: false, kind: null, editing: null });
    if (tab === "coverage") loadCoverage();
    if (tab === "leave") loadLeave();
    if (tab === "fairness") loadFairness();
  };

  // deep-link highlight from advisory popovers.
  // Hash forms: #coverage={id}, #leave={id}, #fairness={id}. We switch the
  // tab on-mount (read-only: just local UI state) and then flash the row.
  useHashHighlight({
    key: "coverage", testidPrefix: "coverage-row-", deps: [coverage],
    onMatch: () => setTab("coverage"),
  });
  useHashHighlight({
    key: "leave", testidPrefix: "leave-row-", deps: [leave],
    onMatch: () => setTab("leave"),
  });
  useHashHighlight({
    key: "fairness", testidPrefix: "fairness-row-", deps: [fairness],
    onMatch: () => setTab("fairness"),
  });

  const archive = async (kind, ruleId) => {
    const path = kind === "coverage" ? "/coverage-rules"
              : kind === "leave"    ? "/leave-rules"
                                     : "/fairness-rules";
    try {
      await api.delete(`${path}/${ruleId}`);
      toast.success("Archived");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const restore = async (kind, ruleId) => {
    const path = kind === "coverage" ? "/coverage-rules"
              : kind === "leave"    ? "/leave-rules"
                                     : "/fairness-rules";
    try {
      await api.patch(`${path}/${ruleId}`, { is_active: true });
      toast.success("Restored");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow">Setup</div>
          <h1 className="font-display text-4xl mt-1">Coverage Policies</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Set the minimum staffing, leave handling and fairness goals that
            shape how your rotas are planned.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RecommendedRulesButton kind={tab} onDone={() => {
            loadCoverage(); loadLeave(); loadFairness();
          }} />
          <Button onClick={() => setEditor({ open: true, kind: tab, editing: null })}
            data-testid={`policies-create-${tab}-btn`}
            className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1" />
            New {tab} policy
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44] mb-5"
        data-testid="policies-disclaimer">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
        <div>
          Policies define your team&apos;s scheduling rules including minimum cover,
          leave limits and fairness goals. Managers always make the final
          decisions; policies guide and warn rather than override.
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <SetupHealthCard page={tab} />
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <TabsList className="bg-white border border-slate-200 h-12 p-1.5 gap-1"
            data-testid="policies-tabs">
            <TabsTrigger value="coverage" data-testid="policies-tab-coverage"
              className="data-[state=active]:bg-[#DBE7FE] data-[state=active]:text-[#1D4ED8] data-[state=active]:shadow-sm rounded-md px-5 h-9 text-sm font-medium">
              Coverage
              <span className="ml-2 text-[11px] tracking-wide text-[#4B5773] bg-slate-100 px-1.5 py-0.5 rounded">
                {coverage.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="leave" data-testid="policies-tab-leave"
              className="data-[state=active]:bg-[#DBE7FE] data-[state=active]:text-[#1D4ED8] data-[state=active]:shadow-sm rounded-md px-5 h-9 text-sm font-medium">
              Leave
              <span className="ml-2 text-[11px] tracking-wide text-[#4B5773] bg-slate-100 px-1.5 py-0.5 rounded">
                {leave.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="fairness" data-testid="policies-tab-fairness"
              className="data-[state=active]:bg-[#DBE7FE] data-[state=active]:text-[#1D4ED8] data-[state=active]:shadow-sm rounded-md px-5 h-9 text-sm font-medium">
              Fairness
              <span className="ml-2 text-[11px] tracking-wide text-[#4B5773] bg-slate-100 px-1.5 py-0.5 rounded">
                {fairness.length}
              </span>
            </TabsTrigger>
          </TabsList>
          <label className="flex items-center gap-2 text-xs text-[#4B5773]">
            <input type="checkbox" data-testid="policies-active-only"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="accent-[#2563EB]" />
            Active only
          </label>
        </div>

        <TabsContent value="coverage">
          <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
            data-testid="coverage-helper-card">
            <div className="text-sm font-semibold text-[#0B1A36] mb-1">What is a Coverage Policy?</div>
            <p className="text-xs text-[#1F2A44] leading-relaxed">
              A coverage policy is the minimum number of qualified people you need scheduled at a given time.
              For example: <em>at least 3 customer support agents Monday to Friday, 09:00–17:00</em>.
              NextRota uses these to flag understaffed shifts and support leave decisions.
            </p>
          </div>
          <Card>
            <table className="w-full text-sm" data-testid="coverage-table">
              <thead>
                <tr className="text-left text-[#4B5773]">
                  <th className="py-3 eyebrow font-normal w-16">P</th>
                  <th className="py-3 eyebrow font-normal">Name</th>
                  <th className="py-3 eyebrow font-normal">Category</th>
                  <th className="py-3 eyebrow font-normal">Days</th>
                  <th className="py-3 eyebrow font-normal">Window</th>
                  <th className="py-3 eyebrow font-normal">Headcount</th>
                  <th className="py-3 eyebrow font-normal">Team</th>
                  <th className="py-3 eyebrow font-normal">Status</th>
                  <th className="py-3 eyebrow font-normal text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {coverage.length === 0 && (
                  <tr><td colSpan={9} className="py-12 text-center"
                          data-testid="coverage-empty">
                    <PolicyEmptyState kind="coverage" onCreated={loadCoverage} />
                  </td></tr>
                )}
                {coverage.map((r) => (
                  <tr key={r.coverage_id}
                      className={r.is_active ? "" : "opacity-50"}
                      data-testid={`coverage-row-${r.coverage_id}`}>
                    <td className="py-3 font-mono text-[11px] text-[#94A3B8]"
                      title="Priority order, lower numbers are evaluated first">P{r.priority}</td>
                    <td className="py-3 font-medium text-[#0B1A36]">{r.name}</td>
                    <td className="py-3 text-[#4B5773]">{r.category_name || "-"}</td>
                    <td className="py-3 text-[#4B5773]">
                      {(r.days_of_week || []).length === 0 ? "All days"
                        : (r.days_of_week).map((d) => DAY_LABELS[d]).join(", ")}
                    </td>
                    <td className="py-3 text-[#4B5773]">
                      {r.start_time || "-"} → {r.end_time || "-"}
                    </td>
                    <td className="py-3 text-[#4B5773]">
                      {r.min_headcount ?? "-"} / {r.max_headcount ?? "-"}
                    </td>
                    <td className="py-3 text-[#4B5773]">{r.team_name || "-"}</td>
                    <td className="py-3">
                      {r.is_active
                        ? <Badge tone="green">Active</Badge>
                        : <Badge tone="slate">Archived</Badge>}
                    </td>
                    <td className="py-3 text-right space-x-1 whitespace-nowrap">
                      <RowActions
                        kind="coverage"
                        rule={r}
                        onPreview={() => setPreview({ open: true, kind: "coverage", rule: r })}
                        onEdit={() => setEditor({ open: true, kind: "coverage", editing: r })}
                        onArchive={() => archive("coverage", r.coverage_id)}
                        onRestore={() => restore("coverage", r.coverage_id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="leave">
          <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
            data-testid="leave-helper-card">
            <div className="text-sm font-semibold text-[#0B1A36] mb-1">What is a Leave Rule?</div>
            <p className="text-xs text-[#1F2A44] leading-relaxed">
              Leave rules are your company&apos;s policies for handling time-off requests, including how many people
              can be off at once, minimum notice required, and blackout periods. NextRota uses these to
              flag conflicts when a leave request is submitted. Managers always make the final decision.
            </p>
          </div>
          <Card>
            <table className="w-full text-sm" data-testid="leave-table">
              <thead>
                <tr className="text-left text-[#4B5773]">
                  <th className="py-3 eyebrow font-normal w-16">P</th>
                  <th className="py-3 eyebrow font-normal">Name</th>
                  <th className="py-3 eyebrow font-normal">Scope</th>
                  <th className="py-3 eyebrow font-normal">Types</th>
                  <th className="py-3 eyebrow font-normal">Concurrency</th>
                  <th className="py-3 eyebrow font-normal">Notice</th>
                  <th className="py-3 eyebrow font-normal">Blackouts</th>
                  <th className="py-3 eyebrow font-normal">Status</th>
                  <th className="py-3 eyebrow font-normal text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leave.length === 0 && (
                  <tr><td colSpan={9} className="py-12 text-center"
                          data-testid="leave-empty">
                    <PolicyEmptyState kind="leave" onCreated={loadLeave} />
                  </td></tr>
                )}
                {leave.map((r) => (
                  <tr key={r.leave_rule_id}
                      className={r.is_active ? "" : "opacity-50"}
                      data-testid={`leave-row-${r.leave_rule_id}`}>
                    <td className="py-3 font-mono text-[11px] text-[#94A3B8]"
                      title="Priority order, lower numbers are evaluated first">P{r.priority}</td>
                    <td className="py-3 font-medium text-[#0B1A36]">{r.name}</td>
                    <td className="py-3">
                      <Badge tone={SCOPE_TONE[r.scope] || "slate"}>{r.scope}</Badge>
                      {r.scope === "team" && r.team_name && (
                        <div className="text-[11px] text-[#4B5773] mt-0.5">{r.team_name}</div>
                      )}
                      {r.scope === "category" && r.category_name && (
                        <div className="text-[11px] text-[#4B5773] mt-0.5">{r.category_name}</div>
                      )}
                    </td>
                    <td className="py-3 text-[#4B5773] text-xs">
                      {(r.leave_types || []).join(", ") || "All"}
                    </td>
                    <td className="py-3 text-[#4B5773] text-xs">
                      {r.max_concurrent_on_leave != null && (
                        <div>Max {r.max_concurrent_on_leave} people</div>
                      )}
                      {r.max_concurrent_pct != null && (
                        <div>Max {r.max_concurrent_pct}%</div>
                      )}
                      {r.max_concurrent_on_leave == null && r.max_concurrent_pct == null && "-"}
                    </td>
                    <td className="py-3 text-[#4B5773]">
                      {r.min_notice_days != null ? `${r.min_notice_days}d` : "-"}
                    </td>
                    <td className="py-3 text-[#4B5773]">{(r.blackout_dates || []).length}</td>
                    <td className="py-3">
                      {r.is_active
                        ? <Badge tone="green">Active</Badge>
                        : <Badge tone="slate">Archived</Badge>}
                    </td>
                    <td className="py-3 text-right space-x-1 whitespace-nowrap">
                      <RowActions
                        kind="leave"
                        rule={r}
                        onPreview={() => setPreview({ open: true, kind: "leave", rule: r })}
                        onEdit={() => setEditor({ open: true, kind: "leave", editing: r })}
                        onArchive={() => archive("leave", r.leave_rule_id)}
                        onRestore={() => restore("leave", r.leave_rule_id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="fairness">
          <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
            data-testid="fairness-helper-card">
            <div className="text-sm font-semibold text-[#0B1A36] mb-1">What is a Fairness Rule?</div>
            <p className="text-xs text-[#1F2A44] leading-relaxed mb-2">
              Fairness rules spread the unpopular work, such as weekends, nights, holidays and overtime, evenly
              across your team. For example: <em>no single person should work more than 20% more Saturdays
              than the team average over a 90-day window</em>. NextRota flags imbalances so managers can
              rebalance the rota.
            </p>
            <p className="text-[11px] text-[#4B5773]">
              Use to ensure fair distribution of weekends, overtime, night shifts, holiday work and assignments.
            </p>
          </div>
          <Card>
            <table className="w-full text-sm" data-testid="fairness-table">
              <thead>
                <tr className="text-left text-[#4B5773]">
                  <th className="py-3 eyebrow font-normal w-16">P</th>
                  <th className="py-3 eyebrow font-normal">Name</th>
                  <th className="py-3 eyebrow font-normal">Scope</th>
                  <th className="py-3 eyebrow font-normal">Metric</th>
                  <th className="py-3 eyebrow font-normal">Window</th>
                  <th className="py-3 eyebrow font-normal">Tolerance</th>
                  <th className="py-3 eyebrow font-normal">Weight</th>
                  <th className="py-3 eyebrow font-normal">Status</th>
                  <th className="py-3 eyebrow font-normal text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fairness.length === 0 && (
                  <tr><td colSpan={9} className="py-12 text-center"
                          data-testid="fairness-empty">
                    <PolicyEmptyState kind="fairness" onCreated={loadFairness} />
                  </td></tr>
                )}
                {fairness.map((r) => (
                  <tr key={r.fairness_id}
                      className={r.is_active ? "" : "opacity-50"}
                      data-testid={`fairness-row-${r.fairness_id}`}>
                    <td className="py-3 font-mono text-[11px] text-[#94A3B8]"
                      title="Priority order, lower numbers are evaluated first">P{r.priority}</td>
                    <td className="py-3 font-medium text-[#0B1A36]">{r.name}</td>
                    <td className="py-3">
                      <Badge tone={SCOPE_TONE[r.scope] || "slate"}>{r.scope}</Badge>
                    </td>
                    <td className="py-3 text-[#4B5773]">
                      {METRIC_LABEL[r.metric] || r.metric}
                    </td>
                    <td className="py-3 text-[#4B5773]">{r.window}</td>
                    <td className="py-3 text-[#4B5773] text-xs">
                      {r.max_deviation_pct != null && <div>±{r.max_deviation_pct}%</div>}
                      {r.max_absolute_gap != null && <div>±{r.max_absolute_gap} abs</div>}
                      {r.max_deviation_pct == null && r.max_absolute_gap == null && "-"}
                    </td>
                    <td className="py-3 text-[#4B5773]">{r.weight}</td>
                    <td className="py-3">
                      {r.is_active
                        ? <Badge tone="green">Active</Badge>
                        : <Badge tone="slate">Archived</Badge>}
                    </td>
                    <td className="py-3 text-right space-x-1 whitespace-nowrap">
                      <RowActions
                        kind="fairness"
                        rule={r}
                        onPreview={() => setPreview({ open: true, kind: "fairness", rule: r })}
                        onEdit={() => setEditor({ open: true, kind: "fairness", editing: r })}
                        onArchive={() => archive("fairness", r.fairness_id)}
                        onRestore={() => restore("fairness", r.fairness_id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {editor.kind === "coverage" && (
        <CoverageRuleEditor open={editor.open} onOpenChange={(v) => setEditor((p) => ({ ...p, open: v }))}
          editing={editor.editing} categories={categories} teams={teams} onSaved={onSaved} />
      )}
      {editor.kind === "leave" && (
        <LeaveRuleEditor open={editor.open} onOpenChange={(v) => setEditor((p) => ({ ...p, open: v }))}
          editing={editor.editing} categories={categories} teams={teams} onSaved={onSaved} />
      )}
      {editor.kind === "fairness" && (
        <FairnessRuleEditor open={editor.open} onOpenChange={(v) => setEditor((p) => ({ ...p, open: v }))}
          editing={editor.editing} categories={categories} teams={teams} onSaved={onSaved} />
      )}

      <PolicyPreviewDialog
        open={preview.open}
        onOpenChange={(v) => setPreview((p) => ({ ...p, open: v }))}
        kind={preview.kind}
        rule={preview.rule}
      />
    </div>
  );
}

function RowActions({ rule, onPreview, onEdit, onArchive, onRestore }) {
  const testRoot = rule.coverage_id || rule.leave_rule_id || rule.fairness_id;
  return (
    <>
      <Button variant="ghost" size="sm" onClick={onPreview}
        data-testid={`policy-preview-${testRoot}`}>
        <Play className="h-4 w-4 text-[#2563EB]" />
      </Button>
      <Button variant="ghost" size="sm" onClick={onEdit}
        data-testid={`policy-edit-${testRoot}`}>
        <Pencil className="h-4 w-4" />
      </Button>
      {rule.is_active ? (
        <Button variant="ghost" size="sm" onClick={onArchive}
          data-testid={`policy-archive-${testRoot}`}>
          <Archive className="h-4 w-4" />
        </Button>
      ) : (
        <Button variant="ghost" size="sm" onClick={onRestore}
          data-testid={`policy-restore-${testRoot}`}>
          <ArchiveRestore className="h-4 w-4" />
        </Button>
      )}
    </>
  );
}

function Badge({ tone = "slate", children }) {
  const TONES = {
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    red:    "bg-red-50 text-red-700 border-red-200",
    blue:   "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    slate:  "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}

// empty-state helper. Shows a calm card with a one-click
// "Generate recommended rules" CTA, plus a small example block so a
// first-time admin sees what a finished rule looks like.

const KIND_LABEL = {
  coverage: "Coverage", leave: "Leave", fairness: "Fairness",
};

const KIND_EXAMPLES = {
  coverage: {
    title: "Example coverage rule",
    body: "Inbound queue, weekdays, Monday to Friday 09:00 to 17:30, minimum of two people on the queue at all times.",
    why: "Tells NextRota the lowest acceptable headcount on a slot so it can flag understaffing before the day starts.",
  },
  leave: {
    title: "Example leave rule",
    body: "Concurrent leave cap, at most one person off at a time during business hours, with at least 14 days notice.",
    why: "Stops two people on the same small team booking the same week off without anyone noticing.",
  },
  fairness: {
    title: "Example fairness goal",
    body: "Weekend shifts per person, keep the gap between any two team members within one shift over a calendar month.",
    why: "Helps the engine rotate weekend cover so the same person isn't always the one giving up Saturday.",
  },
};

function PolicyEmptyState({ kind, onCreated }) {
  const ex = KIND_EXAMPLES[kind];
  const noun = kind === "assignment" ? "assignment rules" : `${kind} policies`;
  return (
    <div className="max-w-2xl mx-auto py-4 text-left">
      <div className="rounded-lg border-2 border-dashed border-[#BFD3FB] bg-[#F0F6FF] p-6 text-left"
           data-testid={`${kind}-empty-card`}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-[#2563EB]" />
          <h3 className="font-medium text-base text-[#0B1A36]">
            No {noun} yet
          </h3>
        </div>
        <p className="text-sm text-[#1F2A44]">
          You can create {noun} yourself, or let NextRota generate a recommended
          starter set based on your business. Existing items are never overwritten.
        </p>
        <div className="mt-4">
          <RecommendedRulesButton kind={kind} onDone={onCreated} prominent />
        </div>
      </div>

      <div className="mt-3 p-4 rounded-lg border border-slate-200 bg-white"
           data-testid={`${kind}-example`}>
        <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-1">{ex.title}</div>
        <p className="text-sm text-[#0B1A36]">{ex.body}</p>
        <p className="text-xs text-[#4B5773] mt-2"><strong>Why this matters:</strong> {ex.why}</p>
      </div>
    </div>
  );
}

function RecommendedRulesButton({ kind, onDone, prominent = false }) {
  const [busy, setBusy] = useState(false);
  const label = kind === "assignment"
    ? "Generate recommended assignment rules"
    : `Generate recommended ${kind} policies`;
  async function run() {
    setBusy(true);
    try {
      const status = await api.get("/setup/wizard-status").catch(() => ({ data: {} }));
      const templateId = status.data?.recommended_template_id || "office_small";
      const { data } = await api.post("/setup/wizard-complete", {
        template_id: templateId,
        industry: status.data?.industry,
        size_band: status.data?.size_band,
        only_kinds: [kind],
        dry_run: false,
      });
      const created = (data.counts?.[kind] || 0);
      const scopeLabel = kind === "assignment" ? "assignment rules" : `${kind} policies`;
      if (created > 0) {
        toast.success(`Created ${created} recommended ${scopeLabel}. Existing items were kept untouched.`);
      } else {
        toast(`No new ${scopeLabel} created. Items with matching names already exist.`);
      }
      onDone && onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not generate rules");
    } finally { setBusy(false); }
  }
  return (
    <Button onClick={run} disabled={busy} size={prominent ? "default" : "sm"}
            variant={prominent ? "default" : "outline"}
            className={prominent
              ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              : "h-10 border-[#BFD3FB] text-[#1D4ED8] hover:bg-[#F0F6FF]"}
            data-testid={`policies-generate-${kind}-btn`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            : <Sparkles className="h-3.5 w-3.5 mr-1" />}
      {label}
    </Button>
  );
}

